import { useState, useMemo } from 'react'
import {
  Coins,
  Search,
  ExternalLink,
  Flag,
  FlagOff,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  Activity,
  GraduationCap,
  CheckCircle,
  TrendingUp,
} from 'lucide-react'
import { useTokenManagement, useFlagToken, useUnflagToken, type TokenStatus } from '@/hooks/useTokenManagement'
import { useToast } from '@/hooks/useToast'
import { StatsCard } from '@/components/dashboard/StatsCard'
import { DashboardSectionHeader } from '@/components/dashboard/DashboardSectionHeader'
import { formatDate, formatRelativeTime, formatUsd } from '@/lib/utils'

const PAGE_SIZE = 25

const STATUS_FILTERS: { value: TokenStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'graduated', label: 'Graduated' },
  { value: 'dead', label: 'Dead' },
  { value: 'flagged', label: 'Flagged' },
]

function getStatusBadge(status: TokenStatus) {
  switch (status) {
    case 'active':
      return <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-emerald-500/20 text-emerald-400">Active</span>
    case 'graduated':
      return <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-blue-500/20 text-blue-400">Graduated</span>
    case 'dead':
      return <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground">Dead</span>
    case 'flagged':
      return <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-red-500/20 text-red-400">Flagged</span>
  }
}

function truncateAddress(address: string): string {
  return `${address.slice(0, 4)}...${address.slice(-4)}`
}

export function TokensPage() {
  const { data, isLoading, error } = useTokenManagement()
  const flagToken = useFlagToken()
  const unflagToken = useUnflagToken()
  const { toast } = useToast()

  type SortField = 'created_at' | 'volume' | 'holders' | 'market_cap' | 'volume_24h' | 'trades'
  const [statusFilter, setStatusFilter] = useState<TokenStatus | 'all'>('all')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortAsc, setSortAsc] = useState(false)

  // Filter and sort tokens
  const filteredTokens = useMemo(() => {
    if (!data?.tokens) return []

    let result = data.tokens

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter((t) => t.status === statusFilter)
    }

    // Search filter
    if (search) {
      const q = search.toLowerCase()
      result = result.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.symbol.toLowerCase().includes(q) ||
          t.mintAddress.toLowerCase().includes(q)
      )
    }

    // Sort
    result = [...result].sort((a, b) => {
      let cmp = 0
      switch (sortField) {
        case 'created_at':
          cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          break
        case 'volume':
          cmp = a.volumeTotal - b.volumeTotal
          break
        case 'holders':
          cmp = a.holderCount - b.holderCount
          break
        case 'market_cap':
          cmp = (a.athMarketCapUsd ?? 0) - (b.athMarketCapUsd ?? 0)
          break
        case 'volume_24h':
          cmp = a.volume24h - b.volume24h
          break
        case 'trades':
          cmp = a.tradeCountTotal - b.tradeCountTotal
          break
      }
      return sortAsc ? cmp : -cmp
    })

    return result
  }, [data?.tokens, statusFilter, search, sortField, sortAsc])

  const totalPages = Math.max(1, Math.ceil(filteredTokens.length / PAGE_SIZE))
  const pageTokens = filteredTokens.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  // Summary stats are computed from the FULL token list (unfiltered) so the
  // Overview cards always reflect the platform totals, not whatever the user
  // has currently filtered to.
  const summary = useMemo(() => {
    const allTokens = data?.tokens ?? []
    return {
      total: allTokens.length,
      active: allTokens.filter((t) => t.status === 'active').length,
      graduated: allTokens.filter((t) => t.status === 'graduated').length,
      totalVolume: allTokens.reduce((sum, t) => sum + t.volumeTotal, 0),
    }
  }, [data?.tokens])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortAsc(!sortAsc)
    } else {
      setSortField(field)
      setSortAsc(false)
    }
  }

  const handleFlag = async (mintAddress: string) => {
    const reason = prompt('Reason for flagging this coin:')
    if (reason === null) return
    try {
      await flagToken.mutateAsync({ tokenMint: mintAddress, reason })
      toast({ title: 'Coin flagged', description: 'The coin has been flagged for review.' })
    } catch {
      toast({ title: 'Error', description: 'Failed to flag coin.', variant: 'destructive' })
    }
  }

  const handleUnflag = async (mintAddress: string) => {
    try {
      await unflagToken.mutateAsync(mintAddress)
      toast({ title: 'Flag removed', description: 'The coin flag has been removed.' })
    } catch {
      toast({ title: 'Error', description: 'Failed to remove flag.', variant: 'destructive' })
    }
  }

  if (error) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold">Coins</h2>
          <p className="text-sm text-muted-foreground">View all coins created on dextoro</p>
        </div>
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-400">
          Failed to load coins. Please try again.
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold">Coins</h2>
        <p className="text-sm text-muted-foreground">View all coins created on dextoro</p>
      </div>

      {/* Overview stat cards */}
      <div>
        <DashboardSectionHeader icon={BarChart3} title="Overview" />
        <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-4">
          <StatsCard
            title="Total Coins"
            value={isLoading ? '...' : summary.total.toLocaleString()}
            icon={Coins}
            loading={isLoading}
          />
          <StatsCard
            title="Active Coins"
            value={isLoading ? '...' : summary.active.toLocaleString()}
            icon={Activity}
            loading={isLoading}
          />
          <StatsCard
            title="Graduated Coins"
            value={isLoading ? '...' : summary.graduated.toLocaleString()}
            icon={GraduationCap}
            loading={isLoading}
          />
          <StatsCard
            title="Total Volume"
            value={isLoading ? '...' : `${summary.totalVolume.toFixed(2)} SOL`}
            icon={BarChart3}
            loading={isLoading}
          />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Status tabs */}
        <div className="inline-flex items-center gap-1 rounded-lg bg-muted p-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => { setStatusFilter(f.value); setPage(0) }}
              className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                statusFilter === f.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by name, symbol, or address..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0) }}
            className="w-full rounded-lg border border-border bg-background pl-9 pr-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-brand-fg"
          />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border overflow-x-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-fg border-t-transparent" />
          </div>
        ) : pageTokens.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Coins className="h-8 w-8 mb-2" />
            <p className="text-sm">No coins found</p>
          </div>
        ) : (
          <table className="w-full min-w-[1180px]">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left font-medium text-muted-foreground px-4 py-3 text-sm">Coin</th>
                <th className="text-left font-medium text-muted-foreground px-4 py-3 text-sm">Creator</th>
                <th className="text-left font-medium text-muted-foreground px-4 py-3 text-sm">Status</th>
                <th
                  className="text-right font-medium text-muted-foreground px-4 py-3 text-sm cursor-pointer hover:text-foreground whitespace-nowrap"
                  onClick={() => handleSort('market_cap')}
                >
                  ATH Market Cap {sortField === 'market_cap' ? (sortAsc ? '\u2191' : '\u2193') : ''}
                </th>
                <th
                  className="text-right font-medium text-muted-foreground px-4 py-3 text-sm cursor-pointer hover:text-foreground whitespace-nowrap"
                  onClick={() => handleSort('volume_24h')}
                >
                  24h Volume {sortField === 'volume_24h' ? (sortAsc ? '\u2191' : '\u2193') : ''}
                </th>
                <th
                  className="text-right font-medium text-muted-foreground px-4 py-3 text-sm cursor-pointer hover:text-foreground"
                  onClick={() => handleSort('volume')}
                >
                  Volume {sortField === 'volume' ? (sortAsc ? '\u2191' : '\u2193') : ''}
                </th>
                <th
                  className="text-right font-medium text-muted-foreground px-4 py-3 text-sm cursor-pointer hover:text-foreground"
                  onClick={() => handleSort('trades')}
                >
                  Trades {sortField === 'trades' ? (sortAsc ? '\u2191' : '\u2193') : ''}
                </th>
                <th
                  className="text-right font-medium text-muted-foreground px-4 py-3 text-sm cursor-pointer hover:text-foreground"
                  onClick={() => handleSort('holders')}
                >
                  Holders {sortField === 'holders' ? (sortAsc ? '\u2191' : '\u2193') : ''}
                </th>
                <th className="text-right font-medium text-muted-foreground px-4 py-3 text-sm">Age</th>
                <th
                  className="text-left font-medium text-muted-foreground px-4 py-3 text-sm cursor-pointer hover:text-foreground whitespace-nowrap"
                  onClick={() => handleSort('created_at')}
                >
                  Created {sortField === 'created_at' ? (sortAsc ? '\u2191' : '\u2193') : ''}
                </th>
                <th className="text-right font-medium text-muted-foreground px-4 py-3 text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageTokens.map((token) => {
                const volume24hChange = token.previousVolume24h > 0
                  ? ((token.volume24h - token.previousVolume24h) / token.previousVolume24h) * 100
                  : null
                return (
                <tr key={token.mintAddress} className="border-b border-border last:border-0 hover:bg-muted/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-sm">{token.name}</span>
                      {token.isVerified && (
                        <CheckCircle
                          className="h-3 w-3 text-emerald-400 shrink-0"
                          aria-label="Verified"
                        />
                      )}
                      {token.isTrending && (
                        <TrendingUp
                          className="h-3 w-3 text-amber-400 shrink-0"
                          aria-label="Trending"
                        />
                      )}
                      <span className="text-xs text-muted-foreground ml-0.5">${token.symbol}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {token.creatorUsername || truncateAddress(token.creatorAddress)}
                  </td>
                  <td className="px-4 py-3">{getStatusBadge(token.status)}</td>
                  <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                    {formatUsd(token.athMarketCapUsd)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                    <span>{token.volume24h.toFixed(2)} SOL</span>
                    {volume24hChange !== null && (
                      <span
                        className="ml-1.5 text-xs font-medium"
                        style={{ color: volume24hChange >= 0 ? '#4ADE80' : '#F87171' }}
                      >
                        {volume24hChange >= 0 ? '+' : ''}
                        {volume24hChange.toFixed(0)}%
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                    {token.volumeTotal.toFixed(2)} SOL
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    {token.tradeCountTotal.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    {token.holderCount}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-muted-foreground whitespace-nowrap">
                    {formatRelativeTime(token.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                    {formatDate(token.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {token.isFlagged ? (
                        <button
                          onClick={() => handleUnflag(token.mintAddress)}
                          className="rounded p-1.5 text-red-400 hover:bg-red-500/10"
                          title="Remove flag"
                        >
                          <FlagOff className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleFlag(token.mintAddress)}
                          className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                          title="Flag coin"
                        >
                          <Flag className="h-4 w-4" />
                        </button>
                      )}
                      <a
                        href={`https://launch.dextoro.com/token/${token.mintAddress}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                        title="View on dextoro"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                  </td>
                </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {filteredTokens.length > PAGE_SIZE && (
        <div className="flex items-center justify-between border-t border-border pt-3">
          <span className="text-sm text-muted-foreground">
            {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, filteredTokens.length)} of {filteredTokens.length}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="rounded-lg border border-border px-3 py-1.5 text-sm disabled:opacity-50 hover:bg-muted"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm text-muted-foreground">
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="rounded-lg border border-border px-3 py-1.5 text-sm disabled:opacity-50 hover:bg-muted"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
